import { useEffect } from "react";
import type IJitsiMeetExternalApi from "@jitsi/react-sdk/lib/types/IJitsiMeetExternalApi";
import { useCallModerationState } from "@/zustand/CallModerationState";
import { buildOrionCustomToolbarButtons } from "./jitsiToolbar";
import { featureFlags } from "@/config/featureFlags";

/** Labels for the Orion custom toolbar buttons (translated in the React layer, passed down). */
export type OrionToolbarLabels = {
  chat: string;
  moderation: string;
  minimize: string;
};

/**
 * Makes the `orion-moderation` custom toolbar button MODERATOR-ONLY.
 *
 * Custom toolbar buttons are global (no per-role flag), and the iframe's `customToolbarButtons`
 * config is read once at conference setup — before the local participant's role is known — so the
 * moderation button initially renders for everyone (`buildOrionCustomToolbarButtons` includes it by
 * default). Once the local role is actually observed (`localRoleKnown`), this hook re-applies the
 * config at runtime via the `overwriteConfig` command:
 *   - non-moderator → rebuild WITHOUT the moderation button (removes it).
 *   - moderator     → rebuild WITH it (re-adds it if a prior non-mod/demote state removed it; a
 *                     redundant re-apply at join is idempotent and harmless).
 *
 * REGRESSION-FREE: while the role is still unknown the hook does nothing, so the initial config —
 * which includes the moderation button — stands. A participant who turns out to be a moderator can
 * therefore never lose the button, even if `overwriteConfig` silently no-ops on some build (worst
 * case degrades to the prior behavior: a non-moderator sees a dead, already-click-gated button).
 *
 * `overwriteConfig` re-rendering `customToolbarButtons` reactively in an iframe embed is verified
 * against jitsi-meet source (stable/jitsi-meet_11031) and the deployed bundle (v=9268).
 */
export default function useModerationButtonVisibility(
  apiRef: IJitsiMeetExternalApi | null,
  labels: OrionToolbarLabels,
  /**
   * Which SIBLING custom buttons the runtime re-apply keeps, so the rebuilt
   * `customToolbarButtons` matches the page's INITIAL config. Defaults mirror
   * the in-chat call (`Call.tsx`): chat per the `orionInCallChat` flag,
   * minimize included. The standalone meet page (Product B, `MeetJoinPage`)
   * passes `{ includeChat: false, includeMinimize: false }` — otherwise the
   * re-apply would inject dead buttons its initial config never had.
   */
  options: { includeChat?: boolean; includeMinimize?: boolean } = {},
): void {
  // Primitive deps (never the options object) so callers may pass a fresh
  // object literal each render without re-running the effect.
  const includeChat = options.includeChat ?? featureFlags.orionInCallChat;
  const includeMinimize = options.includeMinimize ?? true;
  // Subscribed as dependencies so the effect RE-RUNS when the local role is discovered or flips.
  const localRoleKnown = useCallModerationState(
    (state) => state.localRoleKnown,
  );
  const localIsModerator = useCallModerationState(
    (state) => state.localIsModerator,
  );

  useEffect(() => {
    if (!apiRef) {
      return;
    }
    // Read the role from the store at EFFECT TIME rather than trusting the render-snapshot values:
    // when `apiRef` changes to a new call, `useModerationEvents`'s cleanup (declared earlier in
    // Call.tsx, so its destroy runs before this effect's setup) has already called
    // `resetModerationState`, so `getState()` here reflects the NEW call. Using the stale render
    // snapshot could apply a PRIOR call's non-moderator role to the new iframe and wrongly strip the
    // moderation button from a moderator. The subscribed values above still drive re-runs.
    const { localRoleKnown: roleKnown, localIsModerator: isModerator } =
      useCallModerationState.getState();
    // Leave the initial config untouched until the local role is actually known — the initial
    // config already includes the moderation button, so a moderator is never at risk.
    if (!roleKnown) {
      return;
    }

    apiRef.executeCommand("overwriteConfig", {
      // Keep the sibling buttons in step with the page's initial config — otherwise a
      // runtime re-apply would re-inject buttons the page never had (e.g. `orion-chat`
      // when the in-call-chat flag is OFF, or minimize on the standalone meet page).
      customToolbarButtons: buildOrionCustomToolbarButtons(labels, {
        includeModeration: isModerator,
        includeChat,
        includeMinimize,
      }),
    });
    // `labels` is memoized by the caller (Call.tsx), so it is a stable reference across renders.
  }, [
    apiRef,
    localRoleKnown,
    localIsModerator,
    labels,
    includeChat,
    includeMinimize,
  ]);
}
